import React from 'react';
import * as Linking from 'expo-linking';
import { getIntl } from '../../../../utils/intl';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import {
    aboutUrl,
    blogUrl,
    careersUrl,
    carinCodeOfConductUrl,
    contactUrl,
    faqUrl,
    helpCenterUrl,
    primaryRecordFacebookUrl,
    primaryRecordHome,
    primaryRecordInstagramUrl,
    primaryRecordLinkedInUrl,
    privacyPolicyUrl,
    statusPageUrl,
    termsOfServiceUrl,
    trustCenterUrl,
} from '../../../../utils/constants';
import Text from '../../../v2BaseComponents/Text';
import PressableDynamicOpacity from '../../../v2BaseComponents/PressableDynamicOpacity';
import Stack from '../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../utils/spacing';
import { Image } from 'react-native';
import Images from '../../../../Images';
import Icon, { IconName } from '../../../v2BaseComponents/Icon';
import Divider from '../../../v2BaseComponents/Divider';
import Button from '../../../v2BaseComponents/Button';

const intl = getIntl('WelcomeScreenFooter', {
    company: 'Company',
    legal: 'Legal',
    home: 'Home',
    about: 'About',
    contact: 'Contact',
    careers: 'Careers',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    disclaimer: '© Primary Record Inc.',
    location: 'Fishers, Indiana',
    faq: 'FAQs',
    product: 'Product',
    helpCenter: 'Help Center',
    applicationStatus: 'Application Status',
    carinCodeOfConduct: 'CARIN Code of Conduct',
    blog: 'Blog',
    trustCenter: 'Trust Center',
    subscribeToTheNewsletter: 'Subscribe to the Newsletter',
});

export type FooterOwnProps = {};

export type FooterProps = FooterOwnProps;

type Section = {
    title: string;
    links: { title: string; href: string }[];
};

const sections: Section[] = [
    {
        title: intl('company'),
        links: [
            { title: intl('home'), href: primaryRecordHome },
            { title: intl('blog'), href: blogUrl },
            { title: intl('careers'), href: careersUrl },
            { title: intl('contact'), href: contactUrl },
            { title: intl('trustCenter'), href: trustCenterUrl },
        ],
    },
    {
        title: intl('product'),
        links: [
            { title: intl('about'), href: aboutUrl },
            { title: intl('helpCenter'), href: helpCenterUrl },
            { title: intl('faq'), href: faqUrl },
            { title: intl('applicationStatus'), href: statusPageUrl },
        ],
    },
    {
        title: intl('legal'),
        links: [
            { title: intl('termsOfService'), href: termsOfServiceUrl },
            { title: intl('privacyPolicy'), href: privacyPolicyUrl },
            {
                title: intl('carinCodeOfConduct'),
                href: carinCodeOfConductUrl,
            },
        ],
    },
];

const Footer: React.FC<FooterProps> = () => {
    const isMobile = useIsMobileLayout();

    const renderSocialIconLink = (name: IconName, url: string) => {
        return (
            <PressableDynamicOpacity
                onPress={() => {
                    Linking.openURL(url);
                }}
            >
                <Icon name={name} size="button-icon" colorVariant="Dark-Text" />
            </PressableDynamicOpacity>
        );
    };

    const imagesStack = (
        <Stack vertical space={4} justify="flex-start" align="center" key={'image-stack'}>
            <Image
                alt="Primary Record Logo"
                source={Images.PrimaryRecordLogoDark}
                defaultSource={Images.PrimaryRecordLogoDark}
                resizeMode="contain"
                style={{ width: 180, height: 24 }}
            />
            <Stack horizontal justify="center" style={{ alignSelf: 'center' }} space={4}>
                {renderSocialIconLink('Facebook', primaryRecordFacebookUrl)}
                {renderSocialIconLink('Instagram', primaryRecordInstagramUrl)}
                {renderSocialIconLink('Linkedin', primaryRecordLinkedInUrl)}
            </Stack>
            <Image
                alt="Nurse Approved"
                source={Images.NurseApproved}
                defaultSource={Images.NurseApproved}
                resizeMode="contain"
                style={{ width: 100, height: 100 }}
            />
            <Image
                alt="Nvidia Inception"
                source={Images.nvidiaInceptionProgramLogo}
                defaultSource={Images.nvidiaInceptionProgramLogo}
                resizeMode="contain"
                style={{ width: 200, height: 73 }}
            />
        </Stack>
    );

    const linkStack = (
        <Stack horizontal style={{ width: isMobile ? '100%' : 500 }} key={'link-stack'} justify="space-around">
            {sections.map((section, index) => (
                <Stack key={index} space={getSpacing(3)} vertical>
                    <Text
                        variant="h6"
                        style={{
                            fontFamily: 'OverpassBold',
                        }}
                    >
                        {section.title}
                    </Text>
                    <Stack space={getSpacing(2)} vertical>
                        {section.links.map((link, index) => (
                            <PressableDynamicOpacity
                                onPress={() => {
                                    Linking.openURL(link.href);
                                }}
                                key={index}
                            >
                                <Text variant="h6">{link.title}</Text>
                            </PressableDynamicOpacity>
                        ))}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );

    const subscribeToNewsletterStacj = (
        <Stack vertical space={4} justify="flex-start" align="center" key={'subscribe-to-newsletter-stack'}>
            <Button
                variant="primary"
                onPress={() => Linking.openURL('https://www.primaryrecord.com/newsletter')}
                title={intl('subscribeToTheNewsletter')}
            />
        </Stack>
    );

    const renderContentStack = () => {
        const content = (
            <>
                {imagesStack}
                {linkStack}
                {subscribeToNewsletterStacj}
            </>
        );

        if (isMobile) {
            return (
                <Stack vertical space={16} justify="flex-start" align="center" fullWidth>
                    {content}
                </Stack>
            );
        } else {
            return (
                <Stack horizontal space={getSpacing(8)} justify="space-between" align="flex-start" fullWidth>
                    {content}
                </Stack>
            );
        }
    };
    return (
        <Stack
            space={getSpacing(8)}
            style={{
                width: '100%',
                marginTop: getSpacing(12),
            }}
            vertical
        >
            {renderContentStack()}
            <Divider horizontal />
            <Stack vertical space={getSpacing(2)} align="center" fullWidth>
                <Text variant="placeholder-text">{intl('disclaimer')}</Text>
                <Text variant="placeholder-text">{intl('location')}</Text>
            </Stack>
        </Stack>
    );
};

export default React.memo(Footer);
